// Mock data for the Flow View page (`/flow-view`).
// Single source for the Process-Intelligence orchestration surface.
// The Flow domain has no backend yet (no D-ORCA collection, no DAITEC
// adapter, no Trusstor primitive); when one lands, swap the import in
// FlowViewPage.tsx for the real loader. Shape mirrors the Lovable
// "Project Command Center → Flow View" prototype 1:1.

import type {
  FlowException,
  FlowOwnership,
  FlowStage,
  FlowStageDetail,
  FlowSummary,
  FlowViewData,
  StageGlyph,
} from '@interfaces/IFlowView';

/* ---------------------------------------------------------------
   Header pills — Flow Health / Overall / Exceptions
--------------------------------------------------------------- */

const HEALTH = {
  flowHealth: 78,
  overall:    57,
  exceptions: 3,
};

/* ---------------------------------------------------------------
   Flow Selector — four flow tiles with a health bar at the foot.
--------------------------------------------------------------- */

const FLOWS: FlowSummary[] = [
  {
    id: 'mep',
    icon: 'mep',
    title: 'MEP Flow',
    stages: 'Mechanical · Electrical · Plumbing — Order to Verify lifecycle',
    health: 78,
  },
  {
    id: 'procurement',
    icon: 'procurement',
    title: 'Procurement Flow',
    stages: 'Quote · PO · Approval · Vendor Confirm · Dispatch',
    health: 86,
  },
  {
    id: 'qa',
    icon: 'qa',
    title: 'Quality Assurance Flow',
    stages: 'Inspection request · Field check · NCR · Resolve · Close',
    health: 91,
  },
  {
    id: 'design',
    icon: 'design',
    title: 'Design-to-Execution Flow',
    stages: 'Design issue · Coordination · Approval · Issued for Construction · Built',
    health: 72,
  },
];

/* ---------------------------------------------------------------
   MEP lifecycle — five stages with progress + due + status.
--------------------------------------------------------------- */

const MEP_STAGES: FlowStage[] = [
  { index: 1, key: 'order',    glyph: 'order',    title: 'Order',    owner: 'Procurement · Atlas Supply',          progress: 100, dueLabel: 'Mar 14', status: 'complete'    },
  { index: 2, key: 'deliver',  glyph: 'deliver',  title: 'Deliver',  owner: 'Logistics · Site Yard',                progress: 64,  dueLabel: 'Mar 22', status: 'delayed'     },
  { index: 3, key: 'schedule', glyph: 'schedule', title: 'Schedule', owner: 'Planning · Site Scheduling',                    progress: 72,  dueLabel: 'Mar 25', status: 'in-progress' },
  { index: 4, key: 'install',  glyph: 'install',  title: 'Install',  owner: 'Precision HVAC · Voltcore Electrical', progress: 38,  dueLabel: 'Apr 08', status: 'in-progress' },
  { index: 5, key: 'verify',   glyph: 'verify',   title: 'Verify',   owner: 'QA · Inspector Lin',                   progress: 12,  dueLabel: 'Apr 14', status: 'pending'     },
];

/* ---------------------------------------------------------------
   Stage Detail — one record per stage. The active stage drives the
   "Stage Detail" panel under the lifecycle row. Deliver is the
   prototype's open-by-default stage and carries the richest
   content; the others use thinner placeholders matched to their
   completion state.
--------------------------------------------------------------- */

function range(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i);
}

const DELIVER_ITEMS = range(6).map((i) => ({
  id: `dlv-${i + 1}`,
  label: `Item ${i + 1} · Deliver`,
  // 27 / 42 on-site: first 4 are done, last 2 outstanding.
  done: i < 4,
}));

const STAGE_DETAILS: Record<StageGlyph, FlowStageDetail> = {
  order: {
    key: 'order',
    title: 'Order',
    owner: 'Procurement · Atlas Supply',
    dueLabel: 'Mar 14',
    completionLabel: '100% · 42/42',
    completionPct: 100,
    dataSource: 'Procurement · POs',
    itemsLabel: 'POs: 42 / 42',
    items: range(6).map((i) => ({ id: `ord-${i + 1}`, label: `PO Line ${i + 1} · Issued`, done: true })),
    blockers: [],
    actionLabel: 'Open Procurement',
    // No procurement page exists yet — Open Procurement is disabled.
    actionPath: null,
  },
  deliver: {
    key: 'deliver',
    title: 'Deliver',
    owner: 'Logistics · Site Yard',
    dueLabel: 'Mar 22',
    completionLabel: '64% · 27/42',
    completionPct: 64,
    dataSource: 'Logistics · Inventory',
    itemsLabel: 'On-Site: 27 / 42',
    items: DELIVER_ITEMS,
    blockers: [
      { id: 'b-dw12',  text: 'Ductwork batch DW-12 delayed 3 days', severity: 'high'   },
      { id: 'b-vav',   text: 'VAV boxes manifest mismatch',         severity: 'medium' },
    ],
    actionLabel: 'Open Logistics',
    actionPath: '/logistics',
  },
  schedule: {
    key: 'schedule',
    title: 'Schedule',
    owner: 'Planning · Site Scheduling',
    dueLabel: 'Mar 25',
    completionLabel: '72% · 13/18',
    completionPct: 72,
    dataSource: 'Planning · Activities',
    itemsLabel: 'Scheduled: 13 / 18',
    items: range(6).map((i) => ({ id: `sch-${i + 1}`, label: `Activity ${i + 1} · Scheduled`, done: i < 4 })),
    blockers: [
      { id: 'b-crane', text: 'L7 crane window conflicts with structural pour 14:00', severity: 'medium' },
    ],
    actionLabel: 'Open Schedule',
    actionPath: '/scheduler',
  },
  install: {
    key: 'install',
    title: 'Install',
    owner: 'Precision HVAC · Voltcore Electrical',
    dueLabel: 'Apr 08',
    completionLabel: '38% · 36/96',
    completionPct: 38,
    dataSource: 'Field Ops · Activities',
    itemsLabel: 'Installed: 36 / 96',
    items: range(6).map((i) => ({ id: `ins-${i + 1}`, label: `Crew Task ${i + 1} · Install`, done: i < 2 })),
    blockers: [
      { id: 'b-crew', text: 'Zone C install crew idling — awaiting DW-12', severity: 'high' },
    ],
    actionLabel: 'Open Activities',
    actionPath: '/activities',
  },
  verify: {
    key: 'verify',
    title: 'Verify',
    owner: 'QA · Inspector Lin',
    dueLabel: 'Apr 14',
    completionLabel: '12% · 11/96',
    completionPct: 12,
    dataSource: 'QA · Inspections',
    itemsLabel: 'Verified: 11 / 96',
    items: range(6).map((i) => ({ id: `vfy-${i + 1}`, label: `Inspection ${i + 1} · Pending`, done: i < 1 })),
    blockers: [],
    actionLabel: 'Open QA',
    actionPath: '/activities',
  },
};

/* ---------------------------------------------------------------
   Exception Layer — open exceptions across stages.
--------------------------------------------------------------- */

const EXCEPTIONS: FlowException[] = [
  {
    id:        'ex-dw12',
    title:     'Ductwork delivery 3 days late',
    body:      'Batch DW-12 idling Zone C install crew (12 workers).',
    stageKey:  'deliver',
    stageLabel:'Deliver',
    severity:  'high',
  },
  {
    id:        'ex-crane',
    title:     'Crane window conflict',
    body:      'L7 crane reservation overlaps with structural pour at 14:00.',
    stageKey:  'schedule',
    stageLabel:'Schedule',
    severity:  'medium',
  },
  {
    id:        'ex-vav',
    title:     'VAV manifest mismatch',
    body:      'PO line 18 shows 14 units, manifest shows 12.',
    stageKey:  'deliver',
    stageLabel:'Deliver',
    severity:  'medium',
  },
];

/* ---------------------------------------------------------------
   Ownership & Accountability — owner per stage with "done" count.
--------------------------------------------------------------- */

const OWNERSHIP: FlowOwnership[] = [
  { key: 'order',    stage: 'Order',    owner: 'Procurement · Atlas Supply',          doneLabel: '42/42 done', status: 'complete'    },
  { key: 'deliver',  stage: 'Deliver',  owner: 'Logistics · Site Yard',                doneLabel: '27/42 done', status: 'delayed'     },
  { key: 'schedule', stage: 'Schedule', owner: 'Planning · Site Scheduling',                    doneLabel: '13/18 done', status: 'in-progress' },
  { key: 'install',  stage: 'Install',  owner: 'Precision HVAC · Voltcore Electrical', doneLabel: '36/96 done', status: 'in-progress' },
  { key: 'verify',   stage: 'Verify',   owner: 'QA · Inspector Lin',                   doneLabel: '11/96 done', status: 'pending'     },
];

export function loadFlowView(): Promise<FlowViewData> {
  return Promise.resolve({
    health:         HEALTH,
    flows:          FLOWS,
    activeFlowId:   'mep',
    stages:         MEP_STAGES,
    activeStageKey: 'deliver',
    stageDetails:   STAGE_DETAILS,
    exceptions:     EXCEPTIONS,
    ownership:      OWNERSHIP,
  });
}
